// /connect — placeholder cards for the four locked third-party
// integrations (CVS / Walgreens pharmacy refill sync, Apple Health,
// Epic / MyChart EHR sync, GoodRx-style coupons). Each card explains
// what the integration will do and lets the user join a waitlist
// (POST /api/me/waitlist later; for V1.1 the buttons just open an
// email to support@otchealthmart.com).

import { SiggyAvatar } from '../components/SiggyAvatar';
import { AppPage } from '../layouts/AppPage';

interface Connect {
  id: string;
  title: string;
  body: string;
  whyBlocked: string;
  icon: JSX.Element;
}

const CONNECTS: Connect[] = [
  {
    id: 'pharmacy',
    title: 'Pharmacy auto-sync',
    body: 'Link your CVS, Walgreens, or Amazon Pharmacy account so refills, prescriptions, and pickup dates flow into MedReview automatically. No more manual entry.',
    whyBlocked:
      'We are working through each pharmacy partner program. Join the waitlist and we will email you when your pharmacy goes live.',
    icon: (
      <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
        <title>Pharmacy</title>
        <rect
          x="3"
          y="8"
          width="18"
          height="13"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M7 8V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path d="M12 12v6M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'apple-health',
    title: 'Apple Health',
    body: 'Read medicines, conditions, and Health Records directly from your iPhone so your list stays current the moment a doctor adds a prescription.',
    whyBlocked:
      'Apple Health access needs the iPhone app, which we are putting on TestFlight this summer. Join the waitlist and we will let you know.',
    icon: (
      <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
        <title>Apple Health</title>
        <path
          d="M12 21s-7-5-7-11a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 6-7 11-7 11z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: 'ehr',
    title: 'MyChart and your hospital',
    body: 'Connect your patient portal (Epic, Cerner, Athena, others) to import your full prescription history with one tap. Siggy instantly knows everything.',
    whyBlocked:
      'Each EHR vendor requires a partner security review. We are in line with the major ones. Join the waitlist to be early.',
    icon: (
      <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
        <title>EHR sync</title>
        <rect
          x="4"
          y="3"
          width="16"
          height="18"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M12 7v4M10 9h4M8 14h8M8 17h5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: 'coupons',
    title: 'Coupons and lowest price',
    body: 'See the best price for each prescription at pharmacies near you, plus a discount card you can show at the counter. GoodRx-style savings, inside MedReview.',
    whyBlocked:
      'Coupon partners require an agreement we are finalizing. Join the waitlist and we will email you the moment savings go live.',
    icon: (
      <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
        <title>Coupons</title>
        <path
          d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2c-1.1 0-2 .9-2 2s.9 2 2 2v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2c1.1 0 2-.9 2-2s-.9-2-2-2z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M15 9l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="9.5" cy="9.5" r="1.2" fill="currentColor" />
        <circle cx="14.5" cy="14.5" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
];

function mailtoLink(id: string): string {
  const subject = encodeURIComponent(`Waitlist: ${id}`);
  const body = encodeURIComponent(
    `Hi Matt and team, please add me to the ${id} waitlist for MedReview. Thanks!`,
  );
  return `mailto:support@otchealthmart.com?subject=${subject}&body=${body}`;
}

export function Connect(): JSX.Element {
  return (
    <AppPage title="Connect" back={{ to: '/account', label: 'Account' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Coming soon</p>
          <p className="food-intro__text">
            These four integrations are next on our roadmap. Each will make MedReview smarter with
            zero typing from you. Tap "Join waitlist" on any one to hear from us first.
          </p>
        </div>
      </section>

      <ul className="connect-list">
        {CONNECTS.map((c) => (
          <li key={c.id} className="connect-card">
            <div className="connect-card__head">
              <span className="connect-card__icon">{c.icon}</span>
              <div>
                <h3 className="connect-card__title">{c.title}</h3>
                <span className="connect-card__pill">Coming soon</span>
              </div>
            </div>
            <p className="connect-card__body">{c.body}</p>
            <p className="connect-card__blocked">{c.whyBlocked}</p>
            <a className="ghost-cta" href={mailtoLink(c.id)}>
              Join the {c.id.replace('-', ' ')} waitlist
            </a>
          </li>
        ))}
      </ul>
    </AppPage>
  );
}
